
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ChatMessage } from "@/lib/types";
import { toast } from "sonner";
import { formatMessages } from "@/lib/formatters/message-formatter";
import { toDatabaseValue } from "@/utils/supabase/type-guards";

export function useChannelMessages(channelId: string | undefined) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  // Fetch messages
  const fetchMessages = async () => {
    if (!channelId) return;
    
    try {
      const { data: messagesData, error: messagesError } = await supabase
        .from("messages")
        .select(`
          *,
          user:user_id (
            id,
            username,
            avatar_id
          ),
          reactions:message_reactions (
            id,
            emoji,
            user_id
          ),
          replyToMessage:reply_to (
            id,
            content,
            user_id,
            user:user_id (
              username
            )
          )
        `)
        .eq("channel_id", toDatabaseValue(channelId))
        .order("created_at", { ascending: true });

      if (messagesError) throw messagesError;

      // Use the formatter utility to format the messages
      const formattedMessages = formatMessages(messagesData || [], channelId);
      setMessages(formattedMessages);
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast.error("Failed to load messages");
    }
  };

  // Initial data fetching and setup realtime
  useEffect(() => {
    if (!channelId) return;
    
    fetchMessages();
    
    // Setup realtime subscriptions
    const messagesChannel = supabase
      .channel(`messages:${channelId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "messages",
          filter: `channel_id=eq.${channelId}`,
        },
        () => {
          fetchMessages();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(messagesChannel);
    };
  }, [channelId]);

  return {
    messages
  };
}
